import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Heart, Check, Gift, Loader2, Sparkles } from 'lucide-react';

const GiftCoachingPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('info');
  const [selectedOption, setSelectedOption] = useState(null);
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [senderName, setSenderName] = useState('');
  const [message, setMessage] = useState('');
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const COMING_SOON_BANNER = (
    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 mb-6">
      <p className="text-sm font-semibold text-amber-800 dark:text-amber-200 text-center">
        A dedicated coach for care partners coming soon!
      </p>
    </div>
  );

  const handleSelectOption = (option) => {
    setSelectedOption(option);
    setStep('details');
  };

  const handleCheckout = async () => {
    if (!recipientName.trim() || !recipientEmail.trim()) {
      toast.error('Please fill in the recipient details');
      return;
    }
    if (!senderName.trim()) {
      toast.error('Please enter your name');
      return;
    }

    setCheckoutLoading(true);
    try {
      const originUrl = window.location.origin;
      const res = await axios.post(`${API}/billing/gift-checkout`, {
        plan_id: selectedOption.id,
        price: selectedOption.price,
        recipient_name: recipientName,
        recipient_email: recipientEmail,
        sender_name: senderName,
        message: message,
        origin_url: originUrl
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
    } finally { setCheckoutLoading(false); }
  };

  return (
    <div className="min-h-screen px-5 py-12" data-testid="gift-coaching-page">
      <div className="max-w-2xl mx-auto">
        <button onClick={() => step === 'details' ? setStep('info') : navigate('/gift-intro')} className="mb-8 text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1.5 text-sm">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        {step === 'info' && (
          <div className="space-y-8">
            {COMING_SOON_BANNER}
            <div className="text-center space-y-6">
              <p className="text-xl sm:text-2xl text-foreground leading-relaxed font-medium">
                When someone is going through cancer, people often ask: <em>What can I do?</em>
              </p>
              <p className="text-lg text-foreground font-semibold">
                This is one clear answer.
              </p>
              <p className="text-base text-muted-foreground leading-relaxed max-w-lg mx-auto">
                Gift a private coaching session with an ICF-certified coach and cancer survivor — so they don't have to navigate it alone.
              </p>
            </div>

            <div className="surface-card rounded-2xl p-6 space-y-4">
              <h3 className="text-lg font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>What they receive</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">A 60-minute 1:1 coaching session to help them:</p>
              <ul className="space-y-2">
                {[
                  'make sense of what\'s happening now',
                  'prepare for medical conversations',
                  'identify next steps and decisions',
                  'get grounded in what they need',
                  'practice communication skills with their team and care partners',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-foreground">
                    <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-muted-foreground italic pt-2 border-t border-border">
                This is structured, professional support — not therapy.
              </p>
            </div>

            <div className="surface-card rounded-2xl p-6 space-y-3">
              <h3 className="text-lg font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>Who it's for</h3>
              <div className="grid grid-cols-2 gap-2">
                {['Newly diagnosed', 'In treatment', 'In survivorship or transition', 'Caregivers or loved ones'].map((item) => (
                  <div key={item} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Heart className="w-3.5 h-3.5 text-cyan-500 flex-shrink-0" />
                    {item}
                  </div>
                ))}
              </div>
            </div>

            <div className="surface-card rounded-2xl p-6 space-y-3">
              <h3 className="text-lg font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>How it works</h3>
              <div className="flex flex-wrap gap-3">
                {['Choose a gift', 'Checkout', 'Receive a gift code', 'Share it with them', 'They book when ready'].map((step, i) => (
                  <div key={step} className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 text-xs font-bold flex items-center justify-center">{i + 1}</span>
                    <span className="text-sm text-foreground">{step}</span>
                    {i < 4 && <span className="text-muted-foreground/40 ml-1">&rarr;</span>}
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground pt-2">No applications. No barriers.</p>
            </div>

            <div className="space-y-4">
              {/* Want to try coaching? - before gift options */}
              <button
                onClick={() => navigate('/coach')}
                className="w-full surface-card rounded-xl p-5 text-center hover:border-cyan-300 dark:hover:border-cyan-700 transition-colors"
                data-testid="try-coaching-btn"
              >
                <p className="text-base font-semibold text-foreground">Want to try coaching?</p>
                <p className="text-xs text-muted-foreground mt-1">Meet Shannon — first session is free</p>
              </button>

              <h3 className="text-lg font-bold text-foreground text-center pt-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Gift options</h3>

              <Card className="surface-card hover:border-cyan-400/50 transition-all cursor-pointer" onClick={() => handleSelectOption({ id: 'coaching_1', name: '1 Coaching Session', price: 299, display: '$299' })} data-testid="coaching-1-btn">
                <CardContent className="p-6 flex items-center justify-between">
                  <div>
                    <h4 className="text-base font-bold text-foreground">$299 — 1 Coaching Session</h4>
                    <p className="text-sm text-muted-foreground mt-0.5">Focused support for a single moment of need</p>
                  </div>
                  <Button className="btn-primary flex-shrink-0">
                    <Gift className="w-4 h-4 mr-1.5" /> Give this gift
                  </Button>
                </CardContent>
              </Card>

              <Card className="surface-card hover:border-emerald-400/50 transition-all cursor-pointer relative overflow-hidden" onClick={() => handleSelectOption({ id: 'coaching_3', name: '3 Coaching Sessions', price: 897, display: '$897' })} data-testid="coaching-3-btn">
                <div className="absolute top-0 right-0 bg-gradient-to-r from-cyan-500 to-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                  <Sparkles className="w-3 h-3 inline mr-1" /> Best Value
                </div>
                <CardContent className="p-6 flex items-center justify-between">
                  <div>
                    <h4 className="text-base font-bold text-foreground">$897 — 3 Coaching Sessions</h4>
                    <p className="text-sm text-muted-foreground mt-0.5">Deeper support over time</p>
                  </div>
                  <Button className="flex-shrink-0 bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-600 hover:to-emerald-600 text-white">
                    <Gift className="w-4 h-4 mr-1.5" /> Give this gift
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {step === 'details' && selectedOption && (
          <div className="max-w-md mx-auto">
            <Card className="surface-card">
              <CardContent className="pt-6 pb-6 px-6 space-y-5">
                <div className="bg-gradient-to-r from-cyan-50 to-emerald-50 dark:from-cyan-900/20 dark:to-emerald-900/20 rounded-xl p-4 text-center">
                  <p className="text-sm font-semibold text-foreground">{selectedOption.name}</p>
                  <p className="text-2xl font-bold text-foreground mt-1">{selectedOption.display}</p>
                </div>

                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-foreground">Who is this gift for?</h3>
                  <div className="space-y-2">
                    <Label htmlFor="recipient-name">Recipient's Name</Label>
                    <Input id="recipient-name" placeholder="Their name" value={recipientName} onChange={(e) => setRecipientName(e.target.value)} data-testid="coaching-recipient-name" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="recipient-email">Recipient's Email</Label>
                    <Input id="recipient-email" type="email" placeholder="their@email.com" value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} data-testid="coaching-recipient-email" />
                  </div>
                </div>

                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-foreground">From</h3>
                  <div className="space-y-2">
                    <Label htmlFor="sender-name">Your Name</Label>
                    <Input id="sender-name" placeholder="Your name" value={senderName} onChange={(e) => setSenderName(e.target.value)} data-testid="coaching-sender-name" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="gift-message">Personal message (optional)</Label>
                    <Input id="gift-message" placeholder="You're not alone in this..." value={message} onChange={(e) => setMessage(e.target.value)} data-testid="coaching-message" />
                  </div>
                </div>

                <Button className="w-full h-12 text-base bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-600 hover:to-emerald-600 text-white" onClick={handleCheckout} disabled={checkoutLoading} data-testid="coaching-checkout-btn">
                  {checkoutLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Gift className="w-4 h-4 mr-2" />}
                  Proceed to Payment
                </Button>

                <button onClick={() => setStep('info')} className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors text-center">
                  Choose a different option
                </button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default GiftCoachingPage;
